import React, { useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  IconButton,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import { DeleteOutline as DeleteOutlineIcon } from '@mui/icons-material';

export const normalizeFollowUpTopics = (value) => {
  if (Array.isArray(value)) {
    return value
      .filter((item) => item && typeof item.text === 'string' && item.text.trim())
      .map((item, index) => ({
        id: item.id || `topic-${index}-${item.text.trim()}`,
        text: item.text.trim(),
        completed: Boolean(item.completed),
      }));
  }

  if (typeof value === 'string' && value.trim().startsWith('[')) {
    try {
      const parsed = JSON.parse(value);
      if (Array.isArray(parsed)) return normalizeFollowUpTopics(parsed);
    } catch {
      // fall through to newline split
    }
  }

  if (typeof value !== 'string') return [];

  return value
    .split(/\r?\n/)
    .map((text) => text.trim())
    .filter(Boolean)
    .map((text, index) => ({
      id: `legacy-topic-${index}-${text}`,
      text,
      completed: false,
    }));
};

export const followUpTopicsToMarkdown = (value) => normalizeFollowUpTopics(value)
  .map((topic) => `- [${topic.completed ? 'x' : ' '}] ${topic.text}`)
  .join('\n');

const FollowUpTopicsChecklist = ({ value, onChange, onDirty }) => {
  const [draft, setDraft] = useState('');
  const topics = normalizeFollowUpTopics(value);

  const updateTopics = (nextTopics) => {
    onChange(nextTopics);
    onDirty?.();
  };

  const addTopic = () => {
    const text = draft.trim();
    if (!text) return;

    updateTopics([
      ...topics,
      {
        id: `topic-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        text,
        completed: false
      }
    ]);
    setDraft('');
  };

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="subtitle2" sx={{ mb: 0.75 }}>
        Topics to read more about
      </Typography>
      <Stack direction="row" spacing={1} alignItems="flex-start">
        <TextField
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter' && !event.shiftKey) {
              event.preventDefault();
              addTopic();
            }
          }}
          placeholder="Enter one topic, question, tool, or link"
          size="small"
          fullWidth
          inputProps={{ 'aria-label': 'New topic to read more about' }}
        />
        <Button variant="outlined" onClick={addTopic} disabled={!draft.trim()}>
          Add
        </Button>
      </Stack>

      {topics.length > 0 ? (
        <Stack spacing={0.25} sx={{ mt: 1 }}>
          {topics.map((topic) => (
            <Stack key={topic.id} direction="row" alignItems="flex-start">
              <FormControlLabel
                control={
                  <Checkbox
                    checked={topic.completed}
                    onChange={() => updateTopics(topics.map((item) => (
                      item.id === topic.id ? { ...item, completed: !item.completed } : item
                    )))}
                  />
                }
                label={topic.text}
                sx={{
                  flex: 1,
                  alignItems: 'flex-start',
                  m: 0,
                  '& .MuiFormControlLabel-label': {
                    pt: 1,
                    overflowWrap: 'anywhere',
                    color: topic.completed ? 'text.secondary' : 'text.primary',
                    textDecoration: topic.completed ? 'line-through' : 'none'
                  }
                }}
              />
              <IconButton
                aria-label={`Remove ${topic.text}`}
                onClick={() => updateTopics(topics.filter((item) => item.id !== topic.id))}
                size="small"
                sx={{ mt: 0.5 }}
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Stack>
          ))}
        </Stack>
      ) : (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Add each topic separately, then tick it when you have finished reading it.
        </Typography>
      )}
    </Box>
  );
};

export default FollowUpTopicsChecklist;
